import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { isCurrentOrHasCompletedProgram } from 'ProgramInclusion';
import { getProgramConfigValue } from 'Program';
import {
    getHasPendingAdmissionOfferOrIsNotYetCurrent,
    getIsCurrentOrHasCompletedProgram,
    getMissingEnglishLanguageProficiencyDocuments,
    getMissingOfficialTranscripts,
    getRecordsIndicateUserShouldUploadEnglishLanguageProficiencyDocuments,
    getRelevantUserProgramStates,
    programInclusionsForTranscriptDownload,
} from 'Users';
import { Text } from 'Text';
import { Divider } from '../../settingsShared/Divider';
import { getTranscriptViewConfig } from '../transcriptViewConfig';
import { EnglishProficiencySection } from './EnglishProficiencySection';
import { TranscriptSection } from './TranscriptSection';
import { UnofficialTranscriptDownloadSection } from './UnofficialTranscriptDownloadSection';
import { MailingAddressSection } from './MailingAddressSection';

// Container for the Settings > Documents tab, replacing the old edit-documents directive. Reads the
// converted currentUser, works out which sections apply, and interleaves dividers between them.
export function EditDocuments() {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();

    const showEnglishLanguageProficiencySection =
        getRecordsIndicateUserShouldUploadEnglishLanguageProficiencyDocuments(currentUser);

    const transcriptViewConfig = getTranscriptViewConfig(currentUser);

    const showMailingAddress = getRelevantUserProgramStates(currentUser).some(ups => {
        const filters = { userProgramState: ups };
        return (
            !!ups.programType &&
            !!getProgramConfigValue(ups.programType, 'requiresMailingAddress') &&
            (getIsCurrentOrHasCompletedProgram(currentUser, filters) ||
                getHasPendingAdmissionOfferOrIsNotYetCurrent(currentUser, filters))
        );
    });

    const showUnofficialTranscriptDownloadSection =
        programInclusionsForTranscriptDownload(currentUser).some(isCurrentOrHasCompletedProgram);

    // On a device that can't upload (an old Cordova build), tell the user to switch to desktop rather
    // than letting them poke at disabled upload buttons.
    const cannotUploadOnThisDevice = window.CORDOVA?.capabilities?.fileUpload === false;
    const showSignInOnDesktopToUploadDocsMessage =
        cannotUploadOnThisDevice &&
        (getMissingOfficialTranscripts(currentUser) || getMissingEnglishLanguageProficiencyDocuments(currentUser));

    const sections: { key: string; node: ReactNode }[] = [];

    if (showEnglishLanguageProficiencySection) {
        sections.push({
            key: 'english-proficiency',
            node: (
                <EnglishProficiencySection
                    currentUser={currentUser}
                    cannotUploadOnThisDevice={cannotUploadOnThisDevice}
                />
            ),
        });
    }

    if (transcriptViewConfig) {
        sections.push({
            key: 'transcripts',
            node: (
                <TranscriptSection
                    config={transcriptViewConfig}
                    currentUser={currentUser}
                    cannotUploadOnThisDevice={cannotUploadOnThisDevice}
                />
            ),
        });
    }

    if (showUnofficialTranscriptDownloadSection) {
        sections.push({
            key: 'transcript-download',
            node: <UnofficialTranscriptDownloadSection currentUser={currentUser} />,
        });
    }

    if (showMailingAddress) {
        sections.push({ key: 'mailing-address', node: <MailingAddressSection currentUser={currentUser} /> });
    }

    // pb-6 on top of the last section's own py-6 gives it the same 48px of breathing room that the
    // sections above get from their trailing divider plus the next section's padding. The empty state
    // has no section to space out, so it would only sit off-centre.
    return (
        <div className={twMerge('px-4 sm:px-10', sections.length > 0 && 'pb-6')}>
            {showSignInOnDesktopToUploadDocsMessage ? (
                <Text as="p" variant="small" className="py-6 text-center">
                    {t('settings.edit_documents.desktop_to_upload')}
                </Text>
            ) : null}

            {sections.length === 0 ? (
                <Text as="p" variant="small" className="py-10 text-center">
                    {t('settings.edit_documents.no_sections_message')}
                </Text>
            ) : (
                sections.map((section, index) => (
                    <Fragment key={section.key}>
                        {index > 0 ? <Divider /> : null}
                        {section.node}
                    </Fragment>
                ))
            )}
        </div>
    );
}
